import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  Signal,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { ControlValueAccessorFn } from '@app/core/state/angularTypes';
import { CheckboxChangeEvent } from 'primeng/checkbox';
import { InputMask } from 'primeng/inputmask';
import { map, OperatorFunction } from 'rxjs';
import { CountryType, isSweden } from 'src/app/core/entity/locale';
import { getTranslationProvider } from 'src/app/core/i18n/getTranslationProvider';
import { TranslatableComponent } from 'src/app/core/i18n/TranslatableComponent';
import { FirmSelectors } from 'src/app/core/state/firm/firm.selectors';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';
import { getCorporateIdentityValidatorByCountry } from '../../misc/corporate-number-validator';

export interface CorporateIdentityFormValueType {
  corporateIdentity: string;
  foreignCorporateIdentity: boolean;
}

@Component({
  template: `
    <label
      [ngClass]="labelClass"
      [for]="foreignCorporateIdentity ? 'corporateIdentityInput' : 'corporateIdentityInputMask'"
      >{{ withScope('orgNumber') | transloco }}</label
    >
    <div class="p-inputgroup corporate-identity-input-group">
      <p-inputMask
        #corporateIdentityInputMask
        inputId="corporateIdentityInputMask"
        [hidden]="foreignCorporateIdentity"
        [mask]="mask()"
        [pTooltip]="corporateIdentityTooltip()"
        tooltipPosition="top"
        [(ngModel)]="corporateIdentity"
        (ngModelChange)="onCorporateIdentityChange($event)"
        (onBlur)="validateCorporateIdentity()"
        (onFocus)="isFieldTouched.set(true)"
        [autoClear]="false"
        [styleClass]="styleClass"
        pasteValidCorporateIdentity
        (pasteError)="showPasteError()" />
      <input
        #corporateIdentityInput
        id="corporateIdentityInput"
        [hidden]="!foreignCorporateIdentity"
        maxlength="11"
        type="text"
        pInputText
        class="corporate-identity-field"
        [ngModel]="corporateIdentity"
        (ngModelChange)="onCorporateIdentityChange($event)" />
      <span class="p-inputgroup-addon bg-neutral-primary border-0 p-2 mr-2 rounded-r-lg">
        <p-checkbox
          [(ngModel)]="foreignCorporateIdentity"
          [binary]="true"
          class="pr-2"
          (onChange)="onForeignCorporateIdentityChange($event)"></p-checkbox>
        {{ withScope('foreign') | transloco }}
      </span>
      <bl-frontend-button
        *ngIf="!foreignCorporateIdentity"
        [disabled]="!foreignCorporateIdentity && (!corporateIdentity || !isValidCorporateIdentity())"
        [text]="withScope(isNewClient ? 'fetch' : 'update') | transloco"
        (onclick)="buttonClick.emit()"></bl-frontend-button>
    </div>
  `,
  selector: 'app-corporate-identity-input',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CorporateIdentityInputComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => CorporateIdentityInputComponent), multi: true },
    getTranslationProvider('corporateIdentityInputComponent'),
  ],
})
export class CorporateIdentityInputComponent extends TranslatableComponent implements ControlValueAccessor, Validator {
  @ViewChild('corporateIdentityInput', { static: true }) corporateIdentityInput: ElementRef;
  @ViewChild('corporateIdentityInputMask', { static: true }) corporateIdentityInputMask: InputMask;

  @Input() isNewClient = true;
  @Input() labelClass: string;

  @Input() foreignCorporateIdentity: boolean;
  @Output() foreignCorporateIdentityChange = new EventEmitter<CheckboxChangeEvent>();

  @Input() corporateIdentity: string;
  @Output() corporateIdentityChange = new EventEmitter<string>();

  @Output() buttonClick = new EventEmitter<void>();

  isFieldDirty = signal(false);
  isFieldTouched = signal(false);
  isValidCorporateIdentity = signal(false);
  corporateIdentityTooltip = signal('');
  mask: Signal<string>;
  orgNumberValidator: Signal<(orgNumber: string) => boolean>;

  private inputReceivedFocus = false;
  private onChange: ControlValueAccessorFn = () => {};
  private onTouched: ControlValueAccessorFn = () => {};

  // To get the right border color on the input field when it's invalid
  get styleClass() {
    const baseClass = 'corporate-identity-field';
    if (this.isValidCorporateIdentity()) {
      return baseClass;
    }
    if (this.isFieldDirty()) {
      return `${baseClass} ng-invalid ng-dirty`;
    }
    return `${baseClass} ng-invalid ng-touched`;
  }

  constructor() {
    super();

    const firmCountryCode$ = this.store.select(FirmSelectors.selectCountryCode);
    this.mask = toSignal(firmCountryCode$.pipe(this.mapCountryCodeToMask()));
    this.orgNumberValidator = toSignal(firmCountryCode$.pipe(this.mapCountryCodeToValidator()));
  }

  withScope(translationKey: string) {
    return `${this.scope}.${translationKey}`;
  }

  focus() {
    if (this.foreignCorporateIdentity) {
      this.setFocusOnCorporateIdentityInput();
      return;
    }
    this.setFocusOnCorporateIdentityMaskInput();
  }

  registerOnChange(fn: ControlValueAccessorFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: ControlValueAccessorFn): void {
    this.onTouched = fn;
  }

  onCorporateIdentityChange(corporateIdentity: string) {
    this.corporateIdentity = corporateIdentity;
    this.onChange({ corporateIdentity, foreignCorporateIdentity: this.foreignCorporateIdentity });
    this.onTouched();
    this.validateCorporateIdentity();
    this.corporateIdentityChange.emit(corporateIdentity);
  }

  onForeignCorporateIdentityChange(event: CheckboxChangeEvent) {
    this.foreignCorporateIdentity = event.checked;
    this.onChange({ corporateIdentity: this.corporateIdentity, foreignCorporateIdentity: event.checked });
    this.onTouched();
    this.isFieldDirty.set(true);
    this.foreignCorporateIdentityChange.emit(event);
  }

  writeValue(obj: CorporateIdentityFormValueType): void {
    if (obj) {
      this.corporateIdentity = obj.corporateIdentity;
      this.foreignCorporateIdentity = obj.foreignCorporateIdentity;
      this.validateCorporateIdentity();
    }
  }

  validate(): ValidationErrors {
    return this.isValidCorporateIdentity() ? null : { corporateIdentity: this.translate('incorrectOrgNumber') };
  }

  validateCorporateIdentity() {
    if (this.foreignCorporateIdentity) {
      this.corporateIdentityTooltip.set('');
      this.isValidCorporateIdentity.set(true);
      return;
    }

    if (!this.corporateIdentity) {
      this.corporateIdentityTooltip.set('');
      return;
    }

    this.isFieldDirty.set(true);
    this.isFieldTouched.set(true);
    this.isValidCorporateIdentity.set(this.orgNumberValidator()(this.corporateIdentity));
    this.corporateIdentityTooltip.update(() =>
      this.isValidCorporateIdentity() ? '' : this.translate('incorrectOrgNumber'),
    );
  }

  showPasteError() {
    this.store.dispatch(
      ToastActions.showWarnMessage({
        summary: this.translate('incorrectOrgNumber'),
        detail: this.translate('pasteErrorMessage'),
      }),
    );
  }

  private setFocusOnCorporateIdentityMaskInput() {
    const inputMaskElement = this.corporateIdentityInputMask?.el.nativeElement;

    // just return if the input has already received focus
    if (!inputMaskElement || this.inputReceivedFocus) {
      return;
    }

    const input: HTMLInputElement = inputMaskElement.querySelector('input');

    if (!input) {
      return;
    }

    // ExpressionChangedAfterItHasBeenCheckedError is thrown in primeng-inputmask if the focus is set directly in ngAfterViewChecked
    setTimeout(() => {
      input.focus();
      this.inputReceivedFocus = document.activeElement === input;
    }, 0);
  }

  private setFocusOnCorporateIdentityInput() {
    // just return if the client isn't using foreign corporate identity or the input has already received focus
    if (!this.corporateIdentityInput?.nativeElement || this.inputReceivedFocus) {
      return;
    }

    this.corporateIdentityInput.nativeElement.focus();
    this.inputReceivedFocus = document.activeElement === this.corporateIdentityInput?.nativeElement;
  }

  private mapCountryCodeToMask(): OperatorFunction<CountryType, string> {
    return map((countryCode: CountryType) => (isSweden(countryCode) ? '999999-9999' : '99999999'));
  }

  private mapCountryCodeToValidator(): OperatorFunction<CountryType, (orgNumber: string) => boolean> {
    return map((countryCode: CountryType) => getCorporateIdentityValidatorByCountry(countryCode));
  }
}
